import {
  GetOtpConfigResponseSchema,
  type GetOtpConfigResponse,
  type UpdateOtpConfigRequest,
  TestSendOtpResponseSchema,
  type TestSendOtpResponse,
  TestVerifyOtpResponseSchema,
  type TestVerifyOtpResponse,
} from "@codway-dev/shared";
import { AuthService } from "./auth.service";

const API_BASE_URL = "/api";

async function authedFetch(
  path: string,
  init?: RequestInit
): Promise<Response> {
  const token = await AuthService.getShopifyToken();
  if (!token) throw new Error("No Shopify token available");
  return fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...init?.headers,
    },
  });
}

async function parseErrorMessage(res: Response): Promise<string> {
  const body = await res.json().catch(() => ({}));
  const error = (body as { error?: { message?: string } | string }).error;
  if (typeof error === "string") return error;
  return error?.message ?? res.statusText;
}

export const OtpConfigService = {
  getConfig: async (): Promise<GetOtpConfigResponse> => {
    const res = await authedFetch("/admin/otp-config");
    if (!res.ok)
      throw new Error(
        `Failed to load OTP config: ${await parseErrorMessage(res)}`
      );
    return GetOtpConfigResponseSchema.parse(await res.json());
  },

  updateConfig: async (
    data: UpdateOtpConfigRequest
  ): Promise<GetOtpConfigResponse> => {
    const res = await authedFetch("/admin/otp-config", {
      method: "PUT",
      body: JSON.stringify(data),
    });
    if (!res.ok)
      throw new Error(
        `Failed to update OTP config: ${await parseErrorMessage(res)}`
      );
    return GetOtpConfigResponseSchema.parse(await res.json());
  },

  sendTestOtp: async (phone: string): Promise<TestSendOtpResponse> => {
    const res = await authedFetch("/admin/otp-config/test-send", {
      method: "POST",
      body: JSON.stringify({ phone }),
    });
    if (!res.ok)
      throw new Error(
        `Failed to send test OTP: ${await parseErrorMessage(res)}`
      );
    return TestSendOtpResponseSchema.parse(await res.json());
  },

  verifyTestOtp: async (
    requestId: string,
    code: string
  ): Promise<TestVerifyOtpResponse> => {
    const res = await authedFetch("/admin/otp-config/test-verify", {
      method: "POST",
      body: JSON.stringify({ request_id: requestId, code }),
    });
    if (!res.ok)
      throw new Error(
        `Failed to verify test OTP: ${await parseErrorMessage(res)}`
      );
    return TestVerifyOtpResponseSchema.parse(await res.json());
  },
};
